import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const C = {
  teal: '#00B4D8', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280', border: '#E5E7EB',
};

const PACKAGE_LABELS = {
  ai_basic: 'AI Practice Only',
  ai_ielts: 'AI IELTS Prep',
  ai_business: 'AI Business English',
  starter_group: 'Group Classes',
  standard: 'Standard',
  premium: 'Premium Group',
  ielts_intensive: 'IELTS Group Intensive',
  corporate: 'Corporate Group',
  online_group: 'Online Group Classes',
  online_standard: 'Online Standard',
  online_1on1: 'Online Premium Group',
  online_ielts: 'Online IELTS Group',
  trial: 'Trial',
};

function PLCard({ title, value, color, icon: Icon, prefix = '$', note }) {
  return (
    <div style={{ padding: 20, background: '#F9FAFB', borderRadius: 12, textAlign: 'center', flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: 18, height: 18, color }} />
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{prefix}{Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
      <div style={{ fontSize: 13, color: C.grey, marginTop: 4 }}>{title}</div>
      {note && <div style={{ fontSize: 11, color: C.grey, marginTop: 2 }}>{note}</div>}
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <Icon style={{ width: 24, height: 24, color: C.grey }} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.dark, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: C.grey }}>{subtitle}</div>
    </div>
  );
}

function RejectModal({ student, onDone, onClose }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error('Please enter a rejection reason');
    setLoading(true);
    try {
      await api.post(`/api/admin/payments/student/${student.id}/reject`, { reason });
      toast.success('Payment proof rejected — student notified to re-upload');
      onDone();
    } catch {
      toast.error('Failed to reject payment');
    } finally { setLoading(false); }
  };
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:440, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin:'0 0 6px', fontSize:17, fontWeight:700, color:C.error }}>Reject Payment Proof</h3>
        <p style={{ margin:'0 0 20px', fontSize:14, color:C.grey }}>
          Reject <strong>{student.full_name}</strong>'s proof and ask them to re-upload the correct document.
        </p>
        <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Reason for rejection <span style={{color:C.error}}>*</span></label>
        <textarea value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Wrong amount shown, image is unclear, document is not a bank transfer receipt…" rows={3}
          style={{ width:'100%', padding:'10px 12px', border:`1.5px solid ${C.error}`, borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box', marginBottom:16 }} />
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={submit} disabled={loading || !reason.trim()}
            style={{ padding:'9px 20px', background:C.error, color:'#fff', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:13, opacity: loading||!reason.trim()?0.6:1 }}>
            {loading ? 'Rejecting…' : '✗ Reject & Request Re-upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ student, onConfirm, onClose }) {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/api/admin/payments/student/${student.id}/confirm`, { notes });
      onConfirm();
    } catch {
      toast.error('Failed to confirm payment');
    } finally { setLoading(false); }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700 }}>Confirm Payment</h3>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: C.grey }}>
          Mark <strong>{student.name}</strong>'s payment of <strong style={{ color: C.success }}>${student.amount}</strong> as received?
        </p>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Notes (optional)</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Cash received, bank transfer ref..." rows={3}
          style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', border: `1px solid ${C.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={submit} disabled={loading} style={{ padding: '9px 20px', background: C.success, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {loading ? 'Confirming…' : 'Confirm Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [tenant, setTenant] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmStudent, setConfirmStudent] = useState(null);
  const [payingTutor, setPayingTutor] = useState(null);
  const [activeTab, setActiveTab] = useState('tutor');
  const [period, setPeriod] = useState('this_month');
  const [studentPayments, setStudentPayments] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null);
  const [rejectStudent, setRejectStudent] = useState(null);
  const [extraBookings, setExtraBookings] = useState([]);
  const [extraLoading, setExtraLoading] = useState(false);
  const [rejectExtra, setRejectExtra] = useState(null);
  const [rejectExtraReason, setRejectExtraReason] = useState('');
  const [requestingPayment, setRequestingPayment] = useState(null);
  const [decliningStudent, setDecliningStudent] = useState(null);
  const [declineModal, setDeclineModal] = useState(null);
  const [payForm, setPayForm] = useState({ bank_name:'', account_holder:'', account_number:'', sort_code:'', swift_bic:'', paypal_email:'', wise_email:'', payment_notes:'', local_payment_info:'', course_title:'', course_description:'', course_url:'', tutor_platform_fee_enabled: false, tutor_platform_fee_percent: 0, tutor_platform_fee_label: 'Platform Fee' });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    if (activeTab === 'extra') {
      setExtraLoading(true);
      api.get('/api/admin/extra-bookings?status=all')
        .then(r => setExtraBookings(r.data.requests || []))
        .catch(() => {})
        .finally(() => setExtraLoading(false));
    }
  }, [activeTab]);

  const confirmExtra = async (id) => {
    try {
      await api.post(`/api/admin/extra-bookings/${id}/confirm`);
      setExtraBookings(prev => prev.map(r => r.id === id ? { ...r, payment_status: 'confirmed' } : r));
      toast.success('Session booked and confirmed!');
    } catch (err) { toast.error(err.response?.data?.error || 'Confirmation failed'); }
  };

  const cancelExtra = async (id) => {
    if (!window.confirm('Cancel this confirmed extra session? The income will be removed from P&L.')) return;
    try {
      // Cancel the TutorBooking via the bookings cancel endpoint - need the booking_id
      // Use the extra-bookings cancel approach: find booking by matching fields
      await api.post(`/api/admin/extra-bookings/${id}/cancel`);
      setExtraBookings(prev => prev.map(r => r.id === id ? { ...r, payment_status: 'cancelled' } : r));
      toast.success('Session cancelled');
    } catch (err) { toast.error(err.response?.data?.error || 'Cancel failed'); }
  };

  const rejectExtraFn = async (id, reason) => {
    try {
      await api.post(`/api/admin/extra-bookings/${id}/reject`, { reason });
      setExtraBookings(prev => prev.map(r => r.id === id ? { ...r, payment_status: 'rejected', rejection_reason: reason } : r));
      setRejectExtra(null); setRejectExtraReason('');
      toast.success('Request rejected');
    } catch { toast.error('Rejection failed'); }
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/dashboard'),
      api.get('/api/admin/payments?period=' + period),
    ])
      .then(([dashRes, payRes]) => {
        setTenant(dashRes.data?.tenant);
        setData(payRes.data);
        setError('');
      api.get('/api/admin/student-payments').then(r => setStudentPayments(r.data || [])).catch(() => {});
      if (!settingsLoaded) {
      api.get('/api/admin/payment-settings').then(r => { setPayForm(f=>({...f,...r.data})); setSettingsLoaded(true); }).catch(() => {});
      }
      })
      .catch(() => setError('Failed to load payment data. Please refresh.'))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfirmPayment = () => {
    setConfirmStudent(null);
    toast.success('Payment confirmed successfully');
    fetchData();
  };

  const handleConfirmStudentPayment = async (studentId) => {
  setConfirmingId(studentId);
  try {
    await api.post(`/api/admin/payments/student/${studentId}/confirm`, {});
    toast.success('Payment confirmed — course unlocked');
    api.get('/api/admin/student-payments').then(r => setStudentPayments(r.data || [])).catch(() => {});
  } catch { toast.error('Failed to confirm payment'); }
  finally { setConfirmingId(null); }
};

  const [unlockingId, setUnlockingId] = useState(null);
  const handleRequestPayment = async (studentId, studentName) => {
    setRequestingPayment(studentId);
    try {
      const r = await api.post(`/api/admin/payments/student/${studentId}/request-payment`);
      toast.success(r.data.message || 'Payment request sent');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send payment request');
    } finally { setRequestingPayment(null); }
  };

  const handleDeclineEnrollment = async (studentId, reason) => {
    setDecliningStudent(studentId);
    try {
      await api.post(`/api/admin/payments/student/${studentId}/decline-enrollment`, { reason });
      toast.success('Enrollment declined — student notified');
      setDeclineModal(null);
      api.get('/api/admin/student-payments').then(r => setStudentPayments(r.data || [])).catch(() => {});
      api.get('/api/admin/payments').then(r => setData(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to decline enrollment');
    } finally { setDecliningStudent(null); }
  };

  const handleUnlockStudent = async (studentId, action, studentName) => {
    const label = action === 'reset' ? 'Reset strikes & give 1 more attempt' : 'Force full repeat course';
    if (!window.confirm(`${label} for ${studentName}?`)) return;
    setUnlockingId(studentId);
    try {
      const r = await api.post(`/api/admin/payments/student/${studentId}/unlock`, { action });
      toast.success(r.data.message || 'Student unlocked');
      api.get('/api/admin/student-payments').then(r => setStudentPayments(r.data || [])).catch(() => {});
    } catch { toast.error('Failed to unlock student'); }
    finally { setUnlockingId(null); }
  };

const handleSaveSettings = async (e) => {
  e.preventDefault();
  setSettingsSaving(true);
  try {
    await api.put('/api/admin/payment-settings', payForm);
    toast.success('Payment settings saved');
  } catch { toast.error('Failed to save settings'); }
  finally { setSettingsSaving(false); }
};

const handlePayTutor = async (tutorId, tutorName) => {
    if (!window.confirm(`Mark all pending earnings for ${tutorName} as paid?`)) return;
    setPayingTutor(tutorId);
    try {
      await api.post(`/api/admin/payments/tutor/${tutorId}/pay`);
      toast.success(`${tutorName}'s payout marked as paid`);
      fetchData();
    } catch {
      toast.error('Failed to mark payout as paid');
    } finally { setPayingTutor(null); }
  };

  const pl = data?.pl || { income: 0, tutor_costs: 0, platform_fee: 0, net_profit: 0 };
  const pendingPayments = data?.pending_payments || [];
  const tutorPayouts = data?.tutor_payouts || [];

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name}>
      {error && (
        <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', color: C.error, fontSize: 14 }}>
          <ExclamationTriangleIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> {error}
        </div>
      )}


      {/* ── Tab Navigation ─────────────────────────────── */}
      <div style={{display:'flex',gap:4,marginBottom:24,borderBottom:'2px solid #E5E7EB',paddingBottom:0}}>
        {[['tutor','Tutor Payouts'],['students','Student Payments'],['extra','Extra Sessions'],['settings','Payment Settings']].map(([k,lbl])=>(
          <button key={k} onClick={()=>setActiveTab(k)} style={{padding:'8px 18px',border:'none',borderBottom:activeTab===k?'2px solid #00B4D8':'2px solid transparent',background:'none',color:activeTab===k?'#00B4D8':'#6B7280',fontWeight:activeTab===k?700:500,fontSize:14,cursor:'pointer',marginBottom:-2}}>{lbl}</button>
        ))}
      </div>

      {activeTab === 'tutor' && (<>
      {/* P&L Summary */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>P&L Summary</h3>
            <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
              {{ this_month: 'This month', last_month: 'Last month', this_year: 'This year', last_year: 'Last year', all: 'All time' }[period]}
              {pl.income_by_type && (
                <> · Course ${Number(pl.income_by_type.course||0).toFixed(0)} · Mock ${Number(pl.income_by_type.mock_test||0).toFixed(0)} · Extra ${Number(pl.income_by_type.extra_session||0).toFixed(0)}</>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {loading && <span style={{ fontSize: 12, color: C.grey }}>Loading…</span>}
            <select value={period} onChange={e => setPeriod(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, fontWeight: 600, color: C.navy, background: '#fff', cursor: 'pointer' }}>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="this_year">This year</option>
              <option value="last_year">Last year</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <PLCard title="Income" value={pl.income} color={C.success} icon={ArrowTrendingUpIcon} />
          <PLCard title="Tutor Costs" value={pl.tutor_costs} color={C.grey} icon={UserGroupIcon} prefix="-$" />
          <PLCard title="Platform Fee" value={pl.platform_fee} color={C.warning} icon={CurrencyDollarIcon} prefix="-$" note="Monthly subscription" />
          <PLCard
            title="Net Profit"
            value={pl.net_profit}
            color={pl.net_profit >= 0 ? C.success : C.error}
            icon={pl.net_profit >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon}
            prefix={pl.net_profit >= 0 ? '$' : '-$'}
          />
        </div>
      </div>

      {/* Pending Student Payments */}
      <div className="card" style={{ overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Pending Payment Confirmations</h3>
            <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>Students awaiting payment verification</div>
          </div>
          {pendingPayments.length > 0 && (
            <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>
              {pendingPayments.length} pending
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: C.grey, fontSize: 14 }}>Loading payments…</div>
        ) : pendingPayments.length === 0 ? (
          <EmptyState icon={CheckCircleIcon} title="All payments up to date" subtitle="No pending payment confirmations right now." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Student', 'Package', 'Amount', 'Date', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.grey, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendingPayments.map((p, i) => (
                <tr key={p.id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={{ padding: '14px 20px', fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '14px 20px', color: C.grey }}>{PACKAGE_LABELS[p.package] || p.package}</td>
                  <td style={{ padding: '14px 20px', fontWeight: 700, color: p.amount > 0 ? C.success : C.grey }}>
                    {p.amount > 0 ? (
                      p.promo ? (
                        <span>
                          <span style={{ textDecoration: 'line-through', color: C.grey, fontWeight: 400, marginRight: 6 }}>${Math.round(p.promo.original)}</span>
                          ${p.amount}
                          <div style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>🏷 {p.promo.code} (−${Math.round(p.promo.discount)})</div>
                        </span>
                      ) : `$${p.amount}`
                    ) : <span style={{ color: C.grey, fontStyle: 'italic' }}>Not set</span>}
                  </td>
                  <td style={{ padding: '14px 20px', color: C.grey }}>{p.date}</td>
                  <td style={{ padding: '14px 20px' }}>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button onClick={() => handleRequestPayment(p.id, p.name)}
                        disabled={requestingPayment === p.id}
                        title="Send payment reminder email to student"
                        style={{ padding:'6px 12px', background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE', borderRadius:7, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        {requestingPayment === p.id ? '…' : '📧 Request'}
                      </button>
                      <button onClick={() => setConfirmStudent(p)}
                        title="Confirm cash/manual payment received"
                        style={{ padding:'6px 12px', background:C.success, color:'#fff', border:'none', borderRadius:7, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        ✓ Mark Paid
                      </button>
                      <button onClick={() => setDeclineModal(p)}
                        title="Decline enrollment — blocks student account"
                        style={{ padding:'6px 12px', background:'#FEF2F2', color:'#DC2626', border:'1px solid #FECACA', borderRadius:7, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        ✗ Decline
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tutor Payouts */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Tutor Payouts This Month</h3>
            <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>Earnings based on completed lessons</div>
          </div>
          {tutorPayouts.filter(t => !t.paid).length > 0 && (
            <span style={{ background: '#FEE2E2', color: '#991B1B', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>
              {tutorPayouts.filter(t => !t.paid).length} unpaid
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: C.grey, fontSize: 14 }}>Loading tutor payouts…</div>
        ) : tutorPayouts.length === 0 ? (
          <EmptyState icon={ClockIcon} title="No tutor earnings this month" subtitle="Tutor payout records will appear here once lessons are completed." />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Tutor', 'Lessons', 'Gross', 'Platform Fee', 'Net Payout', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.grey, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tutorPayouts.map((t, i) => (
                <tr key={t.tutor_id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={{ padding: '14px 20px', fontWeight: 600 }}>{t.name}</td>
                  <td style={{ padding: '14px 20px' }}>{t.lessons}</td>
                  <td style={{ padding: '14px 20px' }}>${t.gross.toFixed(0)}</td>
                  <td style={{ padding: '14px 20px', color: C.warning }}>-${t.fee.toFixed(0)}</td>
                  <td style={{ padding: '14px 20px', fontWeight: 700, color: C.success }}>${t.net.toFixed(0)}</td>
                  <td style={{ padding: '14px 20px' }}>
                    <span style={{
                      padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      background: t.paid ? '#D1FAE5' : '#FEF3C7',
                      color: t.paid ? '#065F46' : '#92400E',
                    }}>
                      {t.paid ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    {!t.paid ? (
                      <button
                        onClick={() => handlePayTutor(t.tutor_id, t.name)}
                        disabled={payingTutor === t.tutor_id}
                        style={{ padding: '7px 16px', background: C.teal, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: payingTutor === t.tutor_id ? 0.7 : 1 }}>
                        {payingTutor === t.tutor_id ? 'Paying…' : 'Mark Paid'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 13, color: C.grey }}>✓ Done</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </>
      )}


      {/* ── Student Payments Tab ─────────────────────────── */}
      {activeTab === 'students' && (
        <div style={{background:'#fff',borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,0.08)',overflow:'hidden'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:700,color:'#0D1B2A'}}>Student Payments</h3>
            <span style={{fontSize:13,color:'#6B7280'}}>{studentPayments.filter(s=>s.payment_status==='submitted').length} awaiting confirmation</span>
          </div>
          {studentPayments.length === 0 ? (
            <div style={{padding:40,textAlign:'center',color:'#6B7280'}}>No students found.</div>
          ) : studentPayments.map((s,i)=>{
            const sc = {pending:'#F59E0B',submitted:'#3B82F6',paid:'#10B981'}[s.payment_status]||'#6B7280';
            const sb = {pending:'#FEF3C7',submitted:'#DBEAFE',paid:'#D1FAE5'}[s.payment_status]||'#F3F4F6';
            return (
              <div key={s.id} style={{display:'grid',gridTemplateColumns:'1fr 100px 120px auto',gap:16,padding:'14px 20px',borderBottom:i<studentPayments.length-1?'1px solid #E5E7EB':'none',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600,fontSize:14,color:'#0D1B2A'}}>{s.full_name}</div>
                  <div style={{fontSize:12,color:'#6B7280'}}>{s.email} · {s.package} · {s.learning_mode}</div>
                  {s.payment_submitted_at && <div style={{fontSize:11,color:'#6B7280',marginTop:2}}>Submitted: {new Date(s.payment_submitted_at).toLocaleDateString()}</div>}
                  {s.payment_rejection_reason && s.payment_status === 'pending' && <div style={{fontSize:11,color:'#EF4444',marginTop:2}}>Rejection reason: {s.payment_rejection_reason}</div>}
                </div>
                <div style={{display:'inline-flex',padding:'3px 10px',borderRadius:20,background:sb,color:sc,fontSize:12,fontWeight:600,justifyContent:'center'}}>{s.payment_status}</div>
                <div style={{display:'flex',gap:6}}>
                  {s.payment_proof_url && (
                    <a href={s.payment_proof_url} target="_blank" rel="noopener noreferrer" style={{padding:'5px 10px',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',color:'#374151',fontSize:12,textDecoration:'none',fontWeight:500}}>View Proof</a>
                  )}
                  {s.payment_status === 'submitted' && !s.course_unlocked && (
                    <>
                      <button onClick={()=>handleConfirmStudentPayment(s.id)} disabled={confirmingId===s.id}
                        style={{padding:'5px 10px',borderRadius:8,border:'none',background:'#10B981',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',opacity:confirmingId===s.id?0.7:1}}>
                        {confirmingId===s.id?'Confirming…':'Confirm ✓'}
                      </button>
                      <button onClick={()=>setRejectStudent(s)} disabled={confirmingId===s.id}
                        style={{padding:'5px 10px',borderRadius:8,border:'none',background:'#EF4444',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                        ✗ Reject
                      </button>
                    </>
                  )}
                  {s.payment_status === 'pending' && s.payment_rejection_reason && (
                    <span style={{fontSize:11,color:'#EF4444',fontWeight:600}}>⚠ Rejected — awaiting re-upload</span>
                  )}
                  {s.course_unlocked && !s.course_locked && <span style={{fontSize:12,color:'#10B981',fontWeight:600}}>✓ Unlocked</span>}
                  {s.course_locked && (
                    <>
                      <button onClick={()=>handleUnlockStudent(s.id,'reset',s.full_name)} disabled={unlockingId===s.id}
                        style={{padding:'5px 10px',borderRadius:8,border:'none',background:'#3B82F6',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                        🔓 Reset & Retry
                      </button>
                      <button onClick={()=>handleUnlockStudent(s.id,'full_repeat',s.full_name)} disabled={unlockingId===s.id}
                        style={{padding:'5px 10px',borderRadius:8,border:'none',background:'#F59E0B',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                        🔁 Full Repeat
                      </button>
                    </>
                  )}
                </div>
                <div style={{fontSize:12,color:'#6B7280',textAlign:'right'}}>
                  {s.course_locked
                    ? <span style={{color:'#EF4444',fontWeight:600}}>🔐 Locked — Review Required</span>
                    : s.course_unlocked ? 'Course active' : 'Locked'}
                  {s.exam_strike_count > 0 && (
                    <span style={{marginLeft:8}}>
                      {[...Array(3)].map((_,i) => (
                        <span key={i} style={{display:'inline-block',width:14,height:14,borderRadius:'50%',background:i<s.exam_strike_count?'#EF4444':'#E5E7EB',marginLeft:3,fontSize:9,lineHeight:'14px',textAlign:'center',color:'#fff',fontWeight:700}}>
                          {i<s.exam_strike_count?'✗':''}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Extra Sessions Tab ──────────────────────────── */}
      {activeTab === 'extra' && (
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <h3 style={{margin:0,fontSize:16,fontWeight:700,color:'#0D1B2A'}}>Extra Session Requests</h3>
            <button onClick={()=>{setExtraLoading(true);api.get('/api/admin/extra-bookings?status=all').then(r=>setExtraBookings(r.data.requests||[])).catch(()=>{}).finally(()=>setExtraLoading(false));}} style={{padding:'7px 16px',background:'#F3F4F6',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,cursor:'pointer'}}>Refresh</button>
          </div>
          {extraLoading ? <div style={{textAlign:'center',padding:40,color:'#6B7280'}}>Loading...</div> : extraBookings.length===0 ? (
            <div style={{background:'#fff',borderRadius:12,padding:40,textAlign:'center',color:'#6B7280'}}>No extra session requests yet.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              {extraBookings.map(r => {
                const sColors={confirmed:'#D1FAE5',submitted:'#FEF9C3',pending:'#EFF6FF',rejected:'#FEE2E2',cancelled:'#F3F4F6'};
                const sTexts={confirmed:'#065F46',submitted:'#92400E',pending:'#1D4ED8',rejected:'#991B1B',cancelled:'#6B7280'};
                return (
                  <div key={r.id} style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 1px 4px rgba(0,0,0,0.06)',border:'1px solid #E5E7EB'}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:14,flexWrap:'wrap'}}>
                      <div style={{width:42,height:42,borderRadius:'50%',background:'#00B4D818',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>{r.lesson_type==='hybrid'?'\uD83E\uDD16':'\uD83D\uDC64'}</div>
                      <div style={{flex:1,minWidth:200}}>
                        <div style={{fontWeight:700,fontSize:14,color:'#1F2937'}}>{r.student_name} <span style={{fontWeight:400,fontSize:12,color:'#6B7280'}}>{r.student_email}</span></div>
                        <div style={{fontSize:13,color:'#6B7280',marginTop:2}}>{r.lesson_type==='hybrid'?'Hybrid':'1-on-1 Tutor'} · with {r.tutor_name}</div>
                        <div style={{fontSize:13,color:'#374151',marginTop:4,fontWeight:600}}>{r.booking_date} at {r.time_slot}:00 · {r.duration_minutes} min</div>
                        {r.notes && <div style={{fontSize:12,color:'#6B7280',marginTop:4}}>Notes: {r.notes}</div>}
                        {r.rejection_reason && <div style={{fontSize:12,color:'#991B1B',marginTop:4}}>Rejected: {r.rejection_reason}</div>}
                      </div>
                      <div style={{textAlign:'right',flexShrink:0}}>
                        <div style={{fontWeight:800,fontSize:18,color:'#00B4D8'}}>{r.currency} {Number(r.price).toFixed(2)}</div>
                        <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,background:sColors[r.payment_status]||'#F3F4F6',color:sTexts[r.payment_status]||'#6B7280'}}>{r.payment_status.charAt(0).toUpperCase()+r.payment_status.slice(1)}</span>
                      </div>
                    </div>
                    {r.payment_proof_url && <div style={{marginTop:10,padding:'8px 12px',background:'#F8FAFC',borderRadius:8}}><a href={r.payment_proof_url} target='_blank' rel='noreferrer' style={{fontSize:12,color:'#00B4D8',fontWeight:600}}>View Payment Proof →</a></div>}
                    {(r.payment_status==='submitted'||r.payment_status==='pending') && (
                      <div style={{marginTop:12,display:'flex',gap:10}}>
                        <button onClick={()=>confirmExtra(r.id)} style={{flex:1,padding:'9px',background:'#10B981',color:'#fff',border:'none',borderRadius:8,fontWeight:700,fontSize:13,cursor:'pointer'}}>✓ Confirm &amp; Book Session</button>
                        <button onClick={()=>setRejectExtra(r)} style={{padding:'9px 18px',background:'#FEE2E2',color:'#991B1B',border:'none',borderRadius:8,fontWeight:700,fontSize:13,cursor:'pointer'}}>✗ Reject</button>
                      </div>
                    )}
                    {r.payment_status==='confirmed' && (
                      <div style={{marginTop:12}}>
                        <button onClick={()=>cancelExtra(r.id)} style={{padding:'8px 18px',background:'#F3F4F6',color:'#6B7280',border:'1px solid #E5E7EB',borderRadius:8,fontWeight:600,fontSize:12,cursor:'pointer'}}>✕ Cancel Session</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {rejectExtra && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <div style={{background:'#fff',borderRadius:14,padding:28,width:420,maxWidth:'90vw'}}>
                <h3 style={{margin:'0 0 8px',fontSize:16,fontWeight:700,color:'#DC2626'}}>Reject Extra Booking</h3>
                <p style={{margin:'0 0 14px',fontSize:13,color:'#6B7280'}}>Rejecting <strong>{rejectExtra.student_name}</strong> request for {rejectExtra.booking_date} at {rejectExtra.time_slot}:00.</p>
                <textarea value={rejectExtraReason} onChange={e=>setRejectExtraReason(e.target.value)} placeholder='Reason for rejection' rows={3} style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13,resize:'vertical',boxSizing:'border-box',marginBottom:14}} />
                <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                  <button onClick={()=>{setRejectExtra(null);setRejectExtraReason('');}} style={{padding:'9px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',cursor:'pointer',fontSize:13}}>Cancel</button>
                  <button onClick={()=>rejectExtraFn(rejectExtra.id,rejectExtraReason)} style={{padding:'9px 20px',background:'#DC2626',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:'pointer',fontSize:13}}>Reject</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Payment Settings Tab ─────────────────────────── */}
      {activeTab === 'settings' && (
        <form onSubmit={handleSaveSettings} style={{background:'#fff',borderRadius:12,padding:24,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
          <h3 style={{margin:'0 0 20px',fontSize:16,fontWeight:700,color:'#0D1B2A'}}>School Bank & Payment Details</h3>
          <p style={{margin:'0 0 20px',fontSize:13,color:'#6B7280'}}>These details will be shown to students so they can make bank transfer payments to enrol in a course.</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',gap:16,marginBottom:16}}>
            {[['bank_name','Bank Name','e.g. Barclays'],['account_holder','Account Holder','Full name on account'],['account_number','Account Number','e.g. 12345678'],['sort_code','Sort Code','e.g. 12-34-56'],['swift_bic','SWIFT / BIC','For international transfers'],['paypal_email','PayPal Email','Optional'],['wise_email','Wise Email','Optional']].map(([key,label,ph])=>(
              <div key={key}>
                <label style={{display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6}}>{label}</label>
                <input value={payForm[key]||''} onChange={e=>setPayForm(f=>({...f,[key]:e.target.value}))} placeholder={ph}
                  style={{width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
              </div>
            ))}
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:'block',fontSize:13,fontWeight:600,color:'#065F46',marginBottom:6}}>💳 Local Card Payment (shown first, most prominent)</label>
            <textarea value={payForm.local_payment_info||''} onChange={e=>setPayForm(f=>({...f,local_payment_info:e.target.value}))} rows={2} placeholder="e.g. Bank: TBC · Account name: ... · Card number: 9860 ...."
              style={{width:'100%',padding:'8px 12px',border:'2px solid #10B981',borderRadius:8,fontSize:14,boxSizing:'border-box',resize:'vertical'}}/>
            <div style={{fontSize:12,color:'#6B7280',marginTop:4}}>Shown as a highlighted block above the bank transfer, on the dashboard, enrolment screen and emails. Leave blank to hide.</div>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6}}>Payment Instructions / Notes</label>
            <textarea value={payForm.payment_notes||''} onChange={e=>setPayForm(f=>({...f,payment_notes:e.target.value}))} rows={3} placeholder="e.g. Please use your full name as reference..."
              style={{width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:14,boxSizing:'border-box',resize:'vertical'}}/>
          </div>
          <div style={{borderTop:"1px solid #e5e7eb",marginTop:24,paddingTop:24}}>
            <h4 style={{margin:"0 0 8px",color:"#1f2937",fontSize:15}}>📚 Course Settings</h4>
            <p style={{margin:"0 0 12px",fontSize:13,color:"#6b7280"}}>Shown on the student's My Course page after payment is confirmed.</p>
            <div style={{marginBottom:10}}>
              <label style={{display:"block",fontSize:13,color:"#374151",marginBottom:4}}>Course Title</label>
              <input value={payForm.course_title||''} onChange={e=>setPayForm(f=>({...f,course_title:e.target.value}))} placeholder="e.g. English B1 Course 2024" style={{width:"100%",padding:"8px 12px",border:"1px solid #E5E7EB",borderRadius:8,fontSize:14,boxSizing:"border-box"}} />
            </div>
            <div style={{marginBottom:10}}>
              <label style={{display:"block",fontSize:13,color:"#374151",marginBottom:4}}>Course Description</label>
              <textarea value={payForm.course_description||''} onChange={e=>setPayForm(f=>({...f,course_description:e.target.value}))} rows={3} placeholder="Brief description of the course content..." style={{width:"100%",padding:"8px 12px",border:"1px solid #E5E7EB",borderRadius:8,fontSize:14,boxSizing:"border-box",resize:"vertical"}} />
            </div>
            <div style={{marginBottom:10}}>
              <label style={{display:"block",fontSize:13,color:"#374151",marginBottom:4}}>Course URL</label>
              <input value={payForm.course_url||''} onChange={e=>setPayForm(f=>({...f,course_url:e.target.value}))} placeholder="https://classroom.google.com/... or Notion link" style={{width:"100%",padding:"8px 12px",border:"1px solid #E5E7EB",borderRadius:8,fontSize:14,boxSizing:"border-box"}} />
            </div>
          </div>
          {/* Tutor Platform Fee */}
          <div style={{borderTop:'1px solid #E5E7EB',marginTop:24,paddingTop:24}}>
            <h4 style={{margin:'0 0 6px',color:'#1F2937',fontSize:15}}>⚙️ Tutor Platform Fee</h4>
            <p style={{margin:'0 0 16px',fontSize:13,color:'#6B7280'}}>Deduct a platform fee from tutor gross earnings on each session. Tutors see their net pay after deduction.</p>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:14,fontWeight:600,color:'#374151'}}>
                <input type="checkbox" checked={!!payForm.tutor_platform_fee_enabled}
                  onChange={e=>setPayForm(f=>({...f,tutor_platform_fee_enabled:e.target.checked}))}
                  style={{width:16,height:16,accentColor:'#00B4D8'}} />
                Enable platform fee deduction
              </label>
            </div>
            {payForm.tutor_platform_fee_enabled && (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <div>
                  <label style={{display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6}}>Fee Percentage (%)</label>
                  <input type="number" min="0" max="100" step="0.5"
                    value={payForm.tutor_platform_fee_percent||0}
                    onChange={e=>setPayForm(f=>({...f,tutor_platform_fee_percent:parseFloat(e.target.value)||0}))}
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:14,boxSizing:'border-box'}} />
                  <div style={{fontSize:12,color:'#6B7280',marginTop:4}}>e.g. 10 = 10% deducted from tutor pay</div>
                </div>
                <div>
                  <label style={{display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6}}>Fee Label (shown to tutors)</label>
                  <input value={payForm.tutor_platform_fee_label||'Platform Fee'}
                    onChange={e=>setPayForm(f=>({...f,tutor_platform_fee_label:e.target.value}))}
                    placeholder="e.g. Platform Fee"
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:14,boxSizing:'border-box'}} />
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={settingsSaving}
            style={{padding:'10px 24px',background:'#00B4D8',color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:600,cursor:'pointer',opacity:settingsSaving?0.7:1}}>
            {settingsSaving?'Saving…':'Save Payment Settings'}
          </button>
        </form>
      )}


      {/* Confirm payment modal */}
      {declineModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:420, maxWidth:'90vw' }}>
            <h3 style={{ margin:'0 0 8px', fontSize:16, fontWeight:700, color:'#DC2626' }}>✗ Decline Enrollment</h3>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#6B7280' }}>
              This will block <strong>{declineModal.name}</strong>'s account and notify them by email.
            </p>
            <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Reason <span style={{color:'#EF4444'}}>*</span></label>
            <textarea id="decline-reason-input" placeholder="e.g. Payment not received after 7 days..." rows={3}
              style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }} />
            <div style={{ display:'flex', gap:10, marginTop:16, justifyContent:'flex-end' }}>
              <button onClick={() => setDeclineModal(null)} style={{ padding:'9px 18px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
              <button disabled={decliningStudent === declineModal.id}
                onClick={() => { const r = document.getElementById('decline-reason-input').value.trim(); if (!r) { toast.error('Please enter a reason'); return; } handleDeclineEnrollment(declineModal.id, r); }}
                style={{ padding:'9px 20px', background:'#DC2626', color:'#fff', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:13 }}>
                {decliningStudent === declineModal.id ? 'Declining…' : '✗ Decline & Notify'}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmStudent && (
        <ConfirmModal student={confirmStudent} onConfirm={handleConfirmPayment} onClose={() => setConfirmStudent(null)} />
      )}

      {/* Reject payment modal */}
      {rejectStudent && (
        <RejectModal
          student={rejectStudent}
          onDone={() => {
            setRejectStudent(null);
            api.get('/api/admin/student-payments').then(r => setStudentPayments(r.data || [])).catch(() => {});
          }}
          onClose={() => setRejectStudent(null)}
        />
      )}
    </Layout>
  );
}
